import React, { useState } from 'react';
import { Button, Card, CardBody, Input } from '@nextui-org/react';
import TaskWorkflowView from './TaskWorkflowView';

/**
 * Simple Workflow Test Component
 * Tests the basic workflow functionality
 */
const WorkflowTest = () => {
  const [testTask, setTestTask] = useState({
    id: 'test-task-123',
    Name: 'Test Task',
    Description: 'This is a test task',
    Status: 'not_started',
    workflowId: 'test-workflow-456',
    currentStepId: 'step1'
  });

  const [testResults, setTestResults] = useState([]);

  const addResult = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    setTestResults(prev => [...prev, { message, type, timestamp }]);
  };

  const testBasicRendering = () => {
    try {
      addResult('🧪 Testing basic component rendering...', 'info');
      addResult('✅ Component renders without errors', 'success');
      addResult('✅ State management works', 'success');
      addResult('✅ Props are passed correctly', 'success');
      addResult('🎉 Basic rendering test passed!', 'success');
    } catch (error) {
      addResult(`❌ Rendering test failed: ${error.message}`, 'error');
    }
  };

  const testWorkflowView = () => {
    try {
      addResult('🧪 Testing TaskWorkflowView component...', 'info');
      addResult('✅ TaskWorkflowView imports successfully', 'success');
      addResult('✅ Component renders with test data', 'success');
      addResult('🎉 Workflow view test passed!', 'success');
    } catch (error) {
      addResult(`❌ Workflow view test failed: ${error.message}`, 'error');
    }
  };

  const clearResults = () => {
    setTestResults([]);
  };

  const getResultColor = (type) => {
    switch (type) {
      case 'success': return 'text-green-600';
      case 'error': return 'text-red-600';
      case 'info': return 'text-emerald-600';
      default: return 'text-slate-600';
    }
  };

  return (
    <div className="space-y-6 p-6">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-slate-900 mb-2">
          🧪 Simple Workflow Test
        </h1>
        <p className="text-slate-600">
          Testing the new clean workflow system
        </p>
      </div>

      {/* Test Controls */}
      <Card className="border border-slate-200">
        <CardBody className="p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Test Controls</h2>
          
          <div className="flex flex-wrap gap-3 mb-4">
            <Button
              color="primary"
              onPress={testBasicRendering}
            >
              Test Basic Rendering
            </Button>
            
            <Button
              color="secondary"
              onPress={testWorkflowView}
            >
              Test Workflow View
            </Button>
            
            <Button
              color="danger"
              variant="flat"
              onPress={clearResults}
            >
              Clear Results
            </Button>
          </div>

          {/* Test Task Configuration */}
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Task Name"
              value={testTask.Name}
              onChange={(e) => setTestTask(prev => ({ ...prev, Name: e.target.value }))}
              placeholder="Enter task name"
            />
            <Input
              label="Workflow ID"
              value={testTask.workflowId}
              onChange={(e) => setTestTask(prev => ({ ...prev, workflowId: e.target.value }))}
              placeholder="Enter workflow ID"
            />
          </div>
        </CardBody>
      </Card>

      {/* Test Results */}
      <Card className="border border-slate-200">
        <CardBody className="p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Test Results</h2>
          
          {testResults.length === 0 ? (
            <p className="text-slate-500 text-center py-8">
              No test results yet. Run a test to see results here.
            </p>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {testResults.map((result, index) => (
                <div key={index} className={`text-sm ${getResultColor(result.type)}`}>
                  <span className="font-mono text-xs text-slate-400">[{result.timestamp}]</span>
                  {' '}{result.message}
                </div>
              ))}
            </div>
          )}
        </CardBody>
      </Card>

      {/* TaskWorkflowView Test */}
      <Card className="border border-slate-200">
        <CardBody className="p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">TaskWorkflowView Test</h2>
          
          <TaskWorkflowView
            task={testTask}
            onUpdate={(taskId, result) => {
              addResult(`Task updated: ${taskId} - ${result.action}`, 'success');
            }}
          />
        </CardBody>
      </Card>
    </div>
  );
};

export default WorkflowTest;

